import { Table } from "@tanstack/react-table";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { JobTypes } from "@/constants/roster.constant";
import { DaySheet, IFleetBooking } from "@/interfaces/roster.interface";
import { getSelectedTableRows } from "@/utils/table-utils";

type TableContextType<T> = {
  selectedItems: string[];
  tables:
    | {
        [key: string]: Table<T>;
      }
    | undefined;
  handleAddTable: (key: string, table: Table<T>) => void;
  handleUnSelectAll: () => void;
  setToggle: React.Dispatch<React.SetStateAction<boolean>>;
  getJobTypeKey: (value: string) => keyof typeof JobTypes;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

type returnTypes<T> = {
  TableContextProvider: ({
    children,
  }: {
    children: ReactNode;
  }) => React.JSX.Element;
  useTableContext: () => TableContextType<T>;
};

type TableContextProvider = {
  children: ReactNode;
};

// Create a function to generate the context
const createTableContext = <T,>(): returnTypes<T> => {
  const TableContext = createContext<TableContextType<T>>({
    selectedItems: [],
    tables: {},
    // eslint-disable-next-line no-empty-function
    handleAddTable: () => {},
    // eslint-disable-next-line no-empty-function
    handleUnSelectAll: () => {},
    // eslint-disable-next-line no-empty-function
    setToggle: () => {},
    getJobTypeKey: () => "DAY_CONTRACT_JOB",
    isLoading: false,
    // eslint-disable-next-line no-empty-function
    setIsLoading: () => {},
  });

  const TableContextProvider = ({
    children,
  }: TableContextProvider): React.JSX.Element => {
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [tables, setTables] = useState<{ [key: string]: Table<T> }>({});

    const [toggle, setToggle] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleAddTable = (key: string, table: Table<T>): void => {
      if (tables[key]) {
        return;
      }
      setTables((prevTables) => ({ ...prevTables, [key]: table }));
    };

    const handleUnSelectAll = (): void => {
      for (const key in tables) {
        const singleTable = tables[key];
        singleTable.toggleAllPageRowsSelected(false);
      }
      setSelectedItems([]);
      setToggle(!toggle);
    };

    const getAllSelectedIds = (): string[] => {
      const selectedIds: string[] = [];

      // Iterate over each table in the tables object
      Object.values(tables).forEach((table) => {
        // Call getAllSelectedItems() on each table
        const idsFromTable = getSelectedTableRows(table);

        // Add the IDs from this table to our result array
        selectedIds.push(...idsFromTable);
      });

      return selectedIds;
    };

    const getJobTypeKey = (value: string): keyof typeof JobTypes => {
      const entries = Object.entries(JobTypes) as [
        keyof typeof JobTypes,
        string,
      ][];
      const entry = entries.find(([, val]) => val === value);

      return entry?.[0] ?? "DAY_CONTRACT_JOB";
    };

    useEffect(() => {
      // Get all selected IDs
      const selectedIds = getAllSelectedIds();

      // Update the selectedItems state with the new IDs
      setSelectedItems(selectedIds);
    }, [toggle]);

    return (
      <TableContext.Provider
        value={{
          selectedItems,
          tables,
          handleAddTable,
          handleUnSelectAll,
          setToggle,
          getJobTypeKey,
          isLoading,
          setIsLoading,
        }}
      >
        {children}
      </TableContext.Provider>
    );
  };

  const useTableContext = (): TableContextType<T> => {
    const context = useContext(TableContext);
    if (context === undefined) {
      throw new Error(
        "useTableContext must be used within a TableContextProvider",
      );
    }

    return context;
  };

  return { TableContextProvider, useTableContext };
};

const {
  TableContextProvider: JobTableCtxProvider,
  useTableContext: useJobTableCtx,
} = createTableContext<DaySheet>();

export { createTableContext, JobTableCtxProvider, useJobTableCtx };

const {
  TableContextProvider: FleetTableCtxProvider,
  useTableContext: useFleetTableCtx,
} = createTableContext<IFleetBooking>();

export { FleetTableCtxProvider, useFleetTableCtx };
